import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input} from '@angular/core';
import {FormArray, FormBuilder} from '@angular/forms';
import {SubFormComponent} from '../_subform';
import {ApparatusFormFieldService} from './apparatus-form-field.service';
import {FormHelperService} from '@services/form-helper.service';
import {debounceTime, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ObjectsProvider} from '@providers/objects/objects.provider';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {MeasureUnitProvider} from '@providers/measure-unit/measure-unit.provider';
import {EquipmentEnums} from '@namespaces/equipment.namespace';
import {EquipmentCreateOptionsService} from '@services/equipment-create-options.service';
import {ModelsInterfaces} from '@namespaces/models.namespace';
import {RegulatoryFieldsValueInterface, RegulatoryTuple} from './regulatory-fields-value.interface';
import {ErrorLoggerService} from '@services/error-logger.service';

/**
 * Компонент поверки в форме карточки оборудования
 */

@Component({
    providers: [
        FormHelperService
    ],
    selector: 'apparatus-form-fields',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './apparatus-form-fields.component.html',
    styleUrls: ['./apparatus-form-fields.component.scss']
})
export class ApparatusFormFieldsComponent extends SubFormComponent {

    /**
    * Ключ 'Эта Форма для добавления Образца (Эталона) ?'.
    * Для образцов и эталонов эта форма будет по-умолчанию выбирать все доступные объекты
    * */
    private isAllObjectsByDefault = false;

    /**
    * Доступные для выбора в селекте группы оборудования.
    * Могут быть заданы по-умолчанию
    * */
    public availableGroups: ModelsInterfaces.Group[] = [];

    public readonly equipmentDataService: EquipmentCreateOptionsService;

    public formGroupTemplate = this._fb.group({
        id: [''],
        title: [''],
        country: [''],
        manufacturer: [''],
        type: [''],
        unit_measure_id: [''],
        regulatoryFields: this._fb.array([this.createRegulatoryGroup()]),
        specifications: this._fb.array([this.createSpecification()]),
    });

    public availableTypes = [];

    public types = EquipmentEnums.Type;

    constructor(
        private objectsProvider: ObjectsProvider,
        private service: ApparatusFormFieldService,
        public methodsProvider: MethodsProvider,
        public formHelperService: FormHelperService,
        public measureUnitProvider: MeasureUnitProvider,
        private cd: ChangeDetectorRef,
        private _fb: FormBuilder,
        private logger: ErrorLoggerService,
        injector: Injector,
    ) {
        super();
        this.service.resetTuplesLastValue();

        try {
            this.equipmentDataService = injector.get(EquipmentCreateOptionsService);
        } catch (e) {
            this.logger.log(e, 'Cannot inject EquipmentCreateOptionsService into ApparatusFormFieldsComponent');
        }
    }

    afterInit() {
        if (this.equipmentDataService) {
            this.subs.push(
                this.equipmentDataService.availableTypes$.subscribe(types => {
                    this.availableTypes = types;

                    /**
                     * Если Тип всего один, то выбираем его и дизейблим
                     */
                    if (types.length === 1) {
                        const firstType = types[0];
                        this.form.get('type').patchValue(firstType.id);
                        this.form.get('type').disable();
                    } else {
                        this.form.get('type').enable();
                    }
                }),
                this.equipmentDataService.defaultGroup$.pipe(
                    filter(v => Boolean(v))
                ).subscribe(group => {
                    this.availableGroups = [group];
                    this.setDefaultGroupValue(group);
                }),
                this.equipmentDataService.defaultMethodId$.pipe(
                    filter(v => Boolean(v))
                ).subscribe(methodId => {
                    this.setDefaultMethodValue(methodId);
                }),
                this.equipmentDataService.isAllObjectsByDefault$.subscribe(isAll => {
                    this.isAllObjectsByDefault = isAll;
                })
            );
        } else {
            /**
             * Тут бы дефолтные значения подставить для типов оборудования, но так как времени особо нет,
             * а ситуаций без сервиса конфигураций пока не будет, оставим реализацию на будущее
             */
        }

        this.subs.push(
            this.objectsProvider.data$.pipe(
                filter(() => this.isAllObjectsByDefault),
                map(objects => objects.asRoot)
            ).subscribe(objects => {
                this.setAllObjectsByDefault(objects);
            }),
            this.form.get('regulatoryFields').valueChanges.pipe(
                debounceTime(30),
                map((value: RegulatoryFieldsValueInterface[]) => value.filter(v => v.method_id && v.objectIds.length)),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                map((value: RegulatoryFieldsValueInterface[]) => value.map(v => {
                    return {
                        method_id: +v.method_id,
                        object_ids: v.objectIds.map(o => +o.id)
                    };
                })),
            ).subscribe((value: RegulatoryTuple[]) => {
                this.setAvailableGroups(value);
            })
        );

        this.form.get('regulatoryFields').updateValueAndValidity();
    }

    /**
     * Установить метод как выбранный по-умолчанию
     * @param methodId
     */
    private setDefaultMethodValue(methodId: number) {
        if (!methodId) {
            return;
        }
        const regulatoryControl = <FormArray>this.form.get('regulatoryFields');

        if (!regulatoryControl) {
            return;
        }

        regulatoryControl.controls.forEach(control => {
            control.patchValue({
                method_id: methodId
            });
        });
    }

    /**
     * Установить дефолтные значения для групп
     * @param group
     */
    private setDefaultGroupValue(group: ModelsInterfaces.Group) {
        if (!group) {
            return;
        }

        const specificationsControl = <FormArray>this.form.get('specifications');

        if (!specificationsControl) {
            return;
        }

        specificationsControl.controls.forEach(control => {
            control.patchValue({
                apparatus_group_id: group.id
            });
        });
    }

    private setAllObjectsByDefault(objects: ModelsInterfaces.Object[]) {
        const regulatory = <FormArray>this.form.get('regulatoryFields');

        regulatory.controls.forEach(control => {
            const objectsControl = <FormArray>control.get('objectIds');
            this.setObjectsToForm(objectsControl, objects);
        });
    }

    /*
    * Создает новую группу для Метода и объектов
    * */
    createRegulatoryGroup() {
        const group = this._fb.group({
            method_id: [''],
            objectIds: this._fb.array([])
        });

        /*
        * Заполняем объекты новых записей теми же объектами, что и у первой для всех, кроме первой
        * Для первой записи this.form пустой, так как форма еще не инициализирована
        * */
        if (this.form) {
            const regulatory = <FormArray>this.form.get('regulatoryFields');
            if (regulatory.value.length) {
                const objectsValue = regulatory.value[0].objectIds;
                this.setObjectsToForm(<FormArray>group.get('objectIds'), objectsValue);
            }
        }


        return group;
    }

    /**
     * Создать запись для выбора группы оборудования
     */
    private createSpecification() {
        return this._fb.group({
            apparatus_group_id: [''],
        });
    }

    /**
     * Записывает объекты objects как значение контрола objectsControl
     * @param objectsControl
     * @param objects
     */
    setObjectsToForm(objectsControl: FormArray, objects: ModelsInterfaces.Object[] = []) {
        objectsControl.markAsDirty();

        this.clearObjectsForControl(objectsControl);

        objects.forEach(item => {
            objectsControl.push(this._fb.group(item));
        });
    }

    /**
     * Очищает значение контрола
     * @param objectsControl
     */
    clearObjectsForControl(objectsControl: FormArray) {
        while (objectsControl.value.length) {
            objectsControl.removeAt(0);
        }
    }

    /**
     * Получить список групп для выбора
     */
    private setAvailableGroups(value: RegulatoryTuple[] = []) {
        this.service.getGroupsByMethods(value).toPromise().then((data: ModelsInterfaces.Group[]) => {
            this.availableGroups = data.sort((a, b) => a.title > b.title ? 1 : -1);
            this.resetSelectedGroups();
            this.cd.detectChanges();
        }, (error) => {
            this.availableGroups = [];
            this.resetSelectedGroups();
            this.cd.detectChanges();
        });
    }

    /*
    * Сбрасываем значения выбранных групп при смене или удалении методов контроля, если в досупных группах нет той,
    * которая уже была выбрана
    * */
    private resetSelectedGroups() {
        const specifications = <FormArray>this.form.get('specifications');
        specifications.controls.forEach(control => {
            /*
            * Если выбранная группа сеелкта более не доступна, то скидываем значение селекта, чтобы пользователь выбрал снова
            * */
            const currentGroupId = control.get('apparatus_group_id').value;
            if (!this.availableGroups.find(item => +item.id === +currentGroupId)) {
                control.patchValue({
                    apparatus_group_id: ''
                });
            }
        });
    }

    onTypeChange() {
        const unitField = this.form.get('unit_measure_id');

        if (!unitField) {
            return;
        }

        unitField.patchValue('');
        unitField.markAsTouched();
        unitField.markAsDirty();
        unitField.updateValueAndValidity();
    }
}
